import { resolveQuery, type ResolvedQuery } from './query';
import { resolveLangPath } from './langPath';
import { getSafeSearchParams } from './searchParams';
import type { ResolvedConfigs } from '@/modules/config/types';

export type LayoutFixtureName = 'valid' | 'broken';
export type AnalyticsMode = 'dev' | 'production';
export type ThemeName = 'light' | 'dark' | 'grey';
export type PaymentApiMode = 'commerce' | 'legacy';
export type PaymentTestBuild = 'app' | 'payments-test';

export interface AppConfig {
  environment: string;
  layoutFixture: LayoutFixtureName;
  sentryDsn?: string;
  analyticsMode: AnalyticsMode;
  analyticsApiUrl?: string;
  analyticsWriteKey?: string;
  analyticsHost?: string;
  facebookPixelId?: string;
  googleTagId?: string;
  tiktokPixelId?: string;
  pinterestTagId?: string;
  amplitudeApiKey?: string;
  snapchatPixelId?: string;
  intercom?: { appId: string; apiBase: string };
  posthog?: { apiKey: string; apiHost: string };
  /** VWO ↔ Amplitude integration is opt-in (marked temporary in WebQuiz). */
  vwoEnabled: boolean;
  productName: 'fit';
  onboardingVariant: string;
  query: ResolvedQuery;
  /** Result of `resolveRemoteConfigs`; filled in App before the quiz renders. */
  remoteConfigs: ResolvedConfigs;
  amplitudeExperimentKey?: string;
  firebase?: {
    apiKey?: string;
    authDomain?: string;
    projectId?: string;
    storageBucket?: string;
    messagingSenderId?: string;
    appId?: string;
    measurementId?: string;
  };
  ssoClientIds: { google?: string; microsoft?: string; yahoo?: string };
  apiUrl?: string;
  theme: ThemeName;
  /** Language: `/<lang>/` path prefix, else whitelisted `?lang=`, else `en`. */
  lang: string;
  /** Router base — `'/ru'` when the language came from the path, `''` otherwise. */
  basePath: string;
  /** Filtered query params of the current URL (see `config/searchParams.ts`). */
  search: URLSearchParams;
  apiMode?: PaymentApiMode;
  paymentApiUrl?: string;
  sessionToken?: string;
  appUserId?: string;
  stripePublishableKey?: string;
  applePayDomain?: string;
  /** Sign in with Apple services id (register screen). */
  appleSignInClientId: string;
  paymentReturnUrl?: string;
  paymentTestBuild?: PaymentTestBuild;
  dOfferSlug?: string;
  expectedDOfferSlug?: string;
  expectedDOfferVersion?: string;
  instantAppUrl?: string;
  layoutChunksEnabled?: boolean;
  layoutChunkConcurrency?: number;
}

const asFixture = (value: string | undefined): LayoutFixtureName => (value === 'broken' ? 'broken' : 'valid');
const asAnalyticsMode = (value: string | undefined): AnalyticsMode => (value === 'production' ? 'production' : 'dev');
const asTheme = (value: string | undefined): ThemeName => (value === 'dark' || value === 'grey' ? value : 'light');
const asPaymentApiMode = (value: string | undefined): PaymentApiMode => (value === 'legacy' ? 'legacy' : 'commerce');

const search = getSafeSearchParams();
const langPath = resolveLangPath(typeof window === 'undefined' ? '/' : window.location?.pathname, search.get('lang'));
const query = resolveQuery(search, import.meta.env.VITE_ONBOARDING_VARIANT, {
  production: (import.meta.env.VITE_APP_ENV || import.meta.env.MODE) === 'production',
});

export const appConfig: AppConfig = {
  environment: import.meta.env.VITE_APP_ENV || import.meta.env.MODE,
  layoutFixture: asFixture(import.meta.env.VITE_LAYOUT_FIXTURE),
  sentryDsn: import.meta.env.VITE_SENTRY_DSN || undefined,
  analyticsMode: asAnalyticsMode(import.meta.env.VITE_ANALYTICS_MODE),
  analyticsApiUrl: import.meta.env.VITE_ANALYTICS_API_URL || undefined,
  analyticsWriteKey: import.meta.env.VITE_ANALYTICS_WRITE_KEY || undefined,
  analyticsHost: import.meta.env.VITE_ANALYTICS_HOST || undefined,
  facebookPixelId: import.meta.env.VITE_FB_PIXEL_FIT || undefined,
  googleTagId: import.meta.env.VITE_GOOGLE_TAG_KEY_FIT || undefined,
  tiktokPixelId: import.meta.env.VITE_TIKTOK_FIT || undefined,
  pinterestTagId: import.meta.env.VITE_PINTEREST_FIT || undefined,
  amplitudeApiKey: import.meta.env.VITE_AMPLITUDE_FIT || undefined,
  snapchatPixelId: import.meta.env.VITE_SNAPCHAT_FIT || undefined,
  intercom:
    import.meta.env.VITE_INTERCOM_APP_ID && import.meta.env.VITE_INTERCOM_API
      ? { appId: import.meta.env.VITE_INTERCOM_APP_ID, apiBase: import.meta.env.VITE_INTERCOM_API }
      : undefined,
  // PostHog needs key AND host: without host posthog-js goes to US cloud, without key init hangs.
  posthog:
    import.meta.env.VITE_POSTHOG_FIT && import.meta.env.VITE_POSTHOG_HOST
      ? { apiKey: import.meta.env.VITE_POSTHOG_FIT, apiHost: import.meta.env.VITE_POSTHOG_HOST }
      : undefined,
  vwoEnabled: import.meta.env.VITE_VWO_ENABLED === 'true',
  productName: 'fit',
  onboardingVariant: query.variant,
  query,
  remoteConfigs: { url: {}, amplitude: null, firebase: null, metadata: {} },
  // WebQuiz uses the analytics Amplitude key for Experiment too, so a separate key is optional.
  amplitudeExperimentKey:
    import.meta.env.VITE_AMPLITUDE_EXPERIMENT_KEY_FIT || import.meta.env.VITE_AMPLITUDE_FIT || undefined,
  firebase: import.meta.env.VITE_FIREBASE_FIT_API_KEY
    ? {
        apiKey: import.meta.env.VITE_FIREBASE_FIT_API_KEY,
        authDomain: import.meta.env.VITE_FIREBASE_FIT_AUTH_DOMAIN,
        projectId: import.meta.env.VITE_FIREBASE_FIT_PROJECT_ID,
        storageBucket: import.meta.env.VITE_FIREBASE_FIT_STORAGE_BUCKET,
        messagingSenderId: import.meta.env.VITE_FIREBASE_FIT_MESSAGING_SENDER_ID,
        appId: import.meta.env.VITE_FIREBASE_FIT_APP_ID,
        measurementId: import.meta.env.VITE_FIREBASE_FIT_MEASUREMENT_ID,
      }
    : undefined,
  ssoClientIds: {
    google: import.meta.env.VITE_SSO_GOOGLE_CLIENT_ID || undefined,
    microsoft: import.meta.env.VITE_SSO_MICROSOFT_CLIENT_ID || undefined,
    yahoo: import.meta.env.VITE_SSO_YAHOO_CLIENT_ID || undefined,
  },
  apiUrl: import.meta.env.VITE_API_URL || undefined,
  theme: asTheme(search.get('theme') || import.meta.env.VITE_THEME),
  lang: langPath.lang,
  basePath: langPath.basePath,
  search,
  apiMode: asPaymentApiMode(import.meta.env.VITE_PAYMENT_API_MODE),
  paymentApiUrl: import.meta.env.VITE_API_URL || undefined,
  sessionToken: import.meta.env.VITE_SESSION_TOKEN || undefined,
  appUserId: import.meta.env.VITE_APP_USER_ID || undefined,
  stripePublishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY_FIT || undefined,
  applePayDomain: import.meta.env.VITE_APPLE_PAY_DOMAIN || window.location.hostname,
  appleSignInClientId: import.meta.env.VITE_APPLE_SIGN_IN_CLIENT_ID || 'com.myorganicapps.organicfit.webauth',
  paymentReturnUrl: import.meta.env.VITE_PAYMENT_RETURN_URL || window.location.href,
  paymentTestBuild: import.meta.env.VITE_PAYMENT_TEST_BUILD === 'true' ? 'payments-test' : 'app',
  dOfferSlug: import.meta.env.VITE_D_OFFER_SLUG || 'd-offer',
  expectedDOfferSlug: import.meta.env.VITE_EXPECTED_D_OFFER_SLUG || undefined,
  expectedDOfferVersion: import.meta.env.VITE_EXPECTED_D_OFFER_VERSION || undefined,
  instantAppUrl: import.meta.env.VITE_INSTANT_APP_URL || undefined,
  layoutChunksEnabled: import.meta.env.VITE_LAYOUT_CHUNKS_ENABLED === 'true',
  layoutChunkConcurrency: Math.max(1, Number(import.meta.env.VITE_LAYOUT_CHUNK_CONCURRENCY || 2)),
};

/** Applies remote config before the quiz mounts: Amplitude → Firebase variant overrides query/env. */
export const applyRemoteConfigs = (config: AppConfig, resolved: ResolvedConfigs): AppConfig => {
  config.remoteConfigs = resolved;
  const remote = resolved.amplitude
    ? (['amplitude', resolved.amplitude] as const)
    : resolved.firebase
      ? (['firebase', resolved.firebase] as const)
      : null;
  const variant = remote?.[1].onboardingProperties.variant?.trim();
  if (remote && variant) {
    config.onboardingVariant = variant;
    config.query = { ...config.query, variant, variantSource: remote[0] };
  }
  return config;
};
